/**
 * Clip masks: collision moved off a prop's own footprint and onto an invisible
 * box a tenth smaller.
 *
 * ── What this is for ────────────────────────────────────────────────────────
 *
 * A prop collides with an **axis-aligned box fitted to its whole mesh**, and a
 * mesh is not a box. A warehouse very nearly is, so its box is honest; a water
 * tower is four thin legs under a tank, a crane is a boom over air, a tree is a
 * trunk under a canopy — and for those the box is mostly the space *around* the
 * thing, which a player reads as being stopped by nothing. Shrinking every box
 * by 10% does not fix that (the tower's legs are not 10% of its footprint), but
 * it takes the edge off the worst of it everywhere at once, and it is the one
 * change that needs no per-prop judgement.
 *
 * ── Why it is a second body and not a smaller footprint ─────────────────────
 *
 * Because `halfX`/`halfZ` on a `PropDefinition` are the **contract the art was
 * scaled to fit** — `prop-fit` fails a mesh that does not fill its box, and
 * `buildBodies` deliberately refuses a `clipSize` on a meshed prop for exactly
 * that reason. Editing the catalogue's figures would desync every mesh from its
 * box and put collision the player cannot see back, in the other direction.
 *
 * So the prop keeps its footprint and stops carrying collision: each placement
 * is set `blocksTanks: false` (and, for most families, `blocksShells: false`
 * too — see `SHELL_BLOCKING_MASK` for the one exception), and an invisible clip
 * volume at 90% of the same box, in the same place, at the same yaw, carries
 * the movement collision instead. The mask is an ordinary `PlacedProp` — it
 * appears in the map editor like any other, and can be dragged, resized,
 * re-typed or deleted one at a time, which is the whole point of putting
 * collision somewhere a designer can reach.
 *
 * ── The families ────────────────────────────────────────────────────────────
 *
 * Four lists below, and every prop not named in one of them gets a solid mask.
 * They are lists rather than a rule read off the catalogue because they encode
 * a *design* call about what a player expects to be able to shoot through, not
 * a fact about geometry.
 *
 * ── What this changes about play ────────────────────────────────────────────
 *
 * Two things, both intended and both visible:
 *
 * **A tank can enter the outer 10% of every masked prop.** That is the point —
 * it is the invisible collision being given back — but it does mean a hull will
 * overlap a wall slightly where it used to stop clear of it.
 *
 * **The shoot-through families stop rounds no longer.** A sandbag line, a
 * dragon's-tooth row, an emplacement, a drum stack and a hedgehog all become
 * cover you can fire over and be fired over, which is the pairing
 * `PlacedProp.blocksShells`' own doc calls "a rule players read correctly off
 * the geometry, and it was asked for repeatedly".
 *
 * **One family moves only its movement collision to the mask and keeps its
 * shell-blocking on the object itself** — `SHELL_BLOCKING_MASK`, below. A Hesco
 * barrier and a chain-link fence still stop a round exactly where the model
 * says they should; only the tank-blocking half of their collision moves onto
 * the 90% mask, for the same footprint-vs-mesh reason every other masked prop
 * does. This is *not* the shoot-through pairing above — a round is still
 * stopped — so it earns its own list rather than joining `SHOOT_THROUGH_MASK`,
 * even though both share the movement-only `clip_wall_move` kind.
 */
import {
  PROP_DEFINITIONS,
  type PlacedProp,
  type PropId,
} from './props';

/**
 * How much of a prop's own footprint its mask keeps, per axis.
 *
 * One figure for every axis and every prop. A per-prop shrink would be a
 * hundred judgements nobody has made and nobody could re-derive; this is a
 * blanket easing whose whole justification is that it is uniform.
 */
export const MASK_SCALE = 0.9;

/**
 * Props whose mask stops a tank and lets a round through — `clip_wall_move`.
 *
 * All of them are knee-to-waist-high field works and dumps: a player looking at
 * a sandbag line, a row of dragon's teeth, an emplacement, a drum stack or an
 * anti-tank hedgehog expects to be able to shoot over it, and expects to be
 * shot over it in return. The catalogue cannot express that — `props.test.ts`
 * holds every *definition* to "blocks tanks implies blocks shells", on the
 * argument that a visible box you cannot drive through but can shoot through
 * reads as a bug. A mask is invisible, so there is no geometry to contradict,
 * and the tank-blocking half means no tank can ever sit inside one — which is
 * the pathology (`an unhittable spot`) that rule is really guarding.
 */
export const SHOOT_THROUGH_MASK: readonly PropId[] = [
  'sandbag_wall',
  'sandbag_pit',
  'dragon_teeth',
  'hedgehog_antitank',
  'oil_drums',
  'rusty_oil_barrels',
  'single_oil_barrel',
  // Knee-high precast concrete — the same "stops a tank, not a shell" read as
  // the rest of this family, and asked for by name on `relay`'s own jersey
  // runs. A propId list, not a per-map one (see `clipMasked`'s `scale`
  // parameter for the one thing on `relay` that *is* map-specific), so every
  // other map placing `barrier_jersey` gets the same behaviour rather than a
  // barrier that reads differently depending which map it is standing on.
  'barrier_jersey',
];

/**
 * Props whose mask stops a tank only — the object itself keeps blocking
 * shells, exactly as its catalogue box already does.
 *
 * A Hesco barrier and a chain-link fence are not field works you expect to
 * shoot over: a round should stop where the model says it stops. What moves
 * to the mask is only the *movement* half of their collision, for the same
 * mesh-is-not-a-box reason every masked prop gets one. So `clipMasked` keeps
 * `blocksShells: true` on these placements and clears only `blocksTanks` —
 * the one family where the kept object and its mask split the two flags
 * between them instead of the mask carrying both.
 */
export const SHELL_BLOCKING_MASK: readonly PropId[] = ['hesco_barrier', 'fence_chainlink'];

/**
 * Props that get no mask at all and are made to stop nothing.
 *
 * There is deliberately no clip volume that blocks neither tanks nor shells —
 * `props.ts` argues that a body stopping nothing is not a body — so "drive
 * through *and* shoot through" can only be expressed by having no mask and
 * clearing both flags on the placement.
 *
 * **Bushes and hedgerows, both.** The wider vegetation family that stays
 * genuinely solid is narrower now: tall grass, a coppice stand, an orchard
 * tree — see `UNMASKED` below.
 *
 * **The 2026-09-02 call above — "hedgerows stay solid" — is superseded,
 * 2026-09-24, on direct instruction.** The reasoning it recorded (a hedgerow
 * is the main cover on `flats`, `convoy` and `relay`, and breaking contact by
 * driving into one is a stated mechanic) was real, but the standing decision
 * is now the opposite: every `hedgerow` and `hedgerow_wild` placement, on
 * every map, drives through and shoots through exactly like a bush. Moving
 * them out of `UNMASKED` and into this list is the whole change — `maskKindFor`
 * and `clipMasked` do not distinguish a bush from a hedge, only from the props
 * that stay solid.
 */
export const NO_MASK: readonly PropId[] = [
  'bush_round',
  'bush_bramble',
  'hedgerow',
  'hedgerow_wild',
];

/**
 * Props left exactly as the catalogue has them — no mask, no override.
 *
 * Two groups, both already pass-through, both already doing what they should.
 *
 * **Ground features** you drive over rather than into: a shell crater, a cut
 * stump, a fallen log, a loading ramp. The simulation has no verticality to
 * express "over" with, so they stop nothing, and a mask would make them stop
 * something.
 *
 * **Vegetation that stays solid**: tall grass, a coppice stand, an orchard
 * tree. Hedgerows moved to `NO_MASK` — see its own doc.
 */
export const UNMASKED: readonly PropId[] = [
  'crater_small',
  'stump_cut',
  'log_fallen',
  'loading_ramp',
  'tree_orchard',
  'grass_tall',
  'coppice_stand',
];

/** Which mask a prop gets, or `null` for none. Exported for the tests. */
export function maskKindFor(propId: PropId): PropId | null {
  const def = PROP_DEFINITIONS[propId];
  // An unknown id, or a clip volume itself: never masked. Masking a mask would
  // shrink it by 10% every time a map was re-baked through this.
  if (!def || def.invisible) return null;
  if (UNMASKED.includes(propId) || NO_MASK.includes(propId)) return null;
  if (SHOOT_THROUGH_MASK.includes(propId) || SHELL_BLOCKING_MASK.includes(propId)) {
    return 'clip_wall_move';
  }
  return 'clip_wall';
}

/**
 * Whether this map's props have already had their collision moved onto masks.
 *
 * The map editor needs this and nothing else does: a **baked** map is a flat
 * array of literals, so `clipMasked` no longer runs over it, and a prop dropped
 * into one afterwards arrives carrying its own full-size footprint while every
 * prop around it is noclipped behind a 90% box. It collides with a metre of air
 * its neighbours do not. Caught on `redoubt`, where nine props — three oaks,
 * four hedgehogs, a truck and a bus — had to be masked by hand.
 *
 * **The fingerprint is the noclipped props, not the masks.** Looking for a
 * `clip_wall` placement would also find a **hand-placed clip zone**, which is an
 * ordinary editor feature on an ordinary unmasked map, and would have the editor
 * stamp masks onto a map that has none. What only a masked map has is the other
 * half of the pass: visible props that would normally block movement, explicitly
 * carrying `blocksTanks: false` — `blocksShells` too, for most families, but not
 * for `SHELL_BLOCKING_MASK`'s (see `clipMasked`), so movement alone is the
 * fingerprint.
 *
 * **A ratio rather than "any", for the mirror-image reason.** One hand-noclipped
 * prop is a designer's own decision — `PlacedProp.blocksTanks` exists for it —
 * and on an unmasked map there may be one or two. A masked map has *all* of
 * them: 1,503 masks across the eight maps this first ran on. So the question is
 * "is this the general rule here", and half is a wide margin either side of both
 * populations rather than a threshold anything sits near.
 *
 * Answers `false` for a map with nothing maskable on it at all — `empty` — which
 * is right: there is no established rule to follow, and the first prop placed
 * there should behave as the catalogue says.
 */
export function usesClipMasks(props: readonly PlacedProp[]): boolean {
  let maskable = 0;
  let noclipped = 0;
  for (const placed of props) {
    // `maskKindFor` is the same question `clipMasked` asks, so a prop family that
    // is deliberately never masked (a bush, a hedgerow, a crater) cannot skew
    // this in either direction.
    if (maskKindFor(placed.propId) === null) continue;
    maskable += 1;
    // `blocksTanks === false` alone, not both flags: `SHELL_BLOCKING_MASK`
    // props are masked with `blocksShells` still true, so counting only the
    // fully-noclipped state would undercount a fence-heavy map and could flip
    // this false on one that is, in fact, masked.
    if (placed.blocksTanks === false) noclipped += 1;
  }
  return maskable > 0 && noclipped * 2 > maskable;
}

/**
 * A map's props with collision moved onto masks.
 *
 * Returns the originals — noclipped where they now have a mask, or where they
 * are meant to stop nothing — followed by the masks themselves. Order matters
 * only for reading: the editor lists placements in array order, and a designer
 * scrolling the props list wants the things they placed before the machinery.
 *
 * Pure, and it must stay pure: the client predicts its own movement against
 * these bodies, so a mask the server has and the browser does not is a tank
 * that visibly slides into a wall and is yanked back out.
 *
 * Called from `maps.ts` as `props: [...clipMasked([ … ])]`. The outer literal
 * bracket is not a stylistic choice — the map editor's BAKE anchors on
 * `props: [` and flattens whatever is inside to literals, so writing
 * `props: clipMasked(…)` would leave those maps unbakeable.
 *
 * `scale` defaults to `MASK_SCALE` and exists for the one map that has asked
 * for a different one rather than for general use — `MASK_SCALE`'s own doc is
 * explicit that a per-prop (and, by the same argument, a per-map) shrink is a
 * judgement call nobody has made for the other seven. Pass it only when a map
 * has actually asked for its own figure; every other caller should keep
 * taking the default so a change to `MASK_SCALE` still moves every map that
 * has not opted out.
 */
export function clipMasked(
  props: readonly PlacedProp[],
  scale: number = MASK_SCALE,
): PlacedProp[] {
  const kept: PlacedProp[] = [];
  const masks: PlacedProp[] = [];

  for (const placed of props) {
    // Already noclipped for movement, so that half of its collision is
    // somewhere else — either a mask from a previous pass or a designer's own
    // decision. Masking it again would stack a second box on the first.
    //
    // This is what makes the pass **idempotent**, and the case it guards is
    // real: a BAKE flattens `clipMasked([…])` to literals, masks and noclip
    // flags and all, and the obvious next edit is to wrap the flattened array
    // again. Without this, every prop on that map would quietly gain a second
    // mask.
    //
    // `blocksTanks` alone, not both flags: `SHELL_BLOCKING_MASK` below is
    // masked with `blocksShells` left `true` on the kept object, so requiring
    // both false would fail to recognise an already-masked barrier or fence on
    // a second pass and stack a duplicate mask under it.
    if (placed.blocksTanks === false) {
      kept.push(placed);
      continue;
    }

    const kind = maskKindFor(placed.propId);
    if (kind === null) {
      // No mask. Either it keeps its catalogue behaviour untouched, or — for a
      // bush — it is made to stop nothing at all.
      kept.push(
        NO_MASK.includes(placed.propId)
          ? { ...placed, blocksTanks: false, blocksShells: false }
          : placed,
      );
      continue;
    }

    const def = PROP_DEFINITIONS[placed.propId];
    // Every family clears `blocksTanks` — that is the whole point of a mask.
    // Only `SHELL_BLOCKING_MASK` keeps `blocksShells` true on the object
    // itself; every other family (the plain `clip_wall` default and the
    // shoot-through family alike) has the mask carry both, so the visible prop
    // stops carrying collision entirely.
    const objectBlocksShells = SHELL_BLOCKING_MASK.includes(placed.propId);
    kept.push({ ...placed, blocksTanks: false, blocksShells: objectBlocksShells });
    masks.push({
      propId: kind,
      x: placed.x,
      z: placed.z,
      // The placement's yaw, not the definition's `modelYaw` — that one turns
      // the *mesh* onto the footprint's axes and is already accounted for in
      // `halfX`/`halfZ`. `buildBodies` reads `placed.yaw` for the body, and the
      // mask has to sit on the box, not on the model.
      yaw: placed.yaw,
      clipSize: {
        halfX: round(def.halfX * scale),
        halfZ: round(def.halfZ * scale),
        height: round(def.height * scale),
      },
    });
  }

  return [...kept, ...masks];
}

/**
 * Two decimals, which is the map editor's own coordinate precision.
 *
 * Without it a 10% shrink writes `2.4300000000000002` into a baked map — the
 * float is correct and the file is unreadable, and the editor's `formatCoord`
 * would round it on the next bake anyway, so the value would change under a
 * no-op re-bake.
 */
function round(v: number): number {
  return Math.round(v * 100) / 100;
}
